import { Alert, Button, Card, DatePicker, Form, Input, Select, message } from 'antd';
import dayjs from 'dayjs';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import {
  EducationSectionSelect,
  EducationStudentSelect,
} from '@/pages/education/components/EducationLookupSelects';

type FormValues = {
  student_id: string;
  section_id?: string;
  referral_on: dayjs.Dayjs;
  referral_reason: string;
  source_type: string;
  source_id?: string;
};

export default function InterventionFormPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.intervention.manage') || user?.permissions?.includes('education.admin')
  );

  const create = useMutation({
    mutationFn: (values: FormValues) =>
      api.post<{ id: string }>('/education/intervention/cases', {
        student_id: values.student_id,
        section_id: values.section_id || undefined,
        referral_on: values.referral_on.format('YYYY-MM-DD'),
        referral_reason: values.referral_reason,
        source_type: values.source_type,
        source_id: values.source_id || undefined,
      }),
    onSuccess: (res) => {
      message.success(t('interventionReferralCreated'));
      const id = res.data?.id;
      navigate(id ? `/education/intervention/${id}` : '/education/intervention');
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  if (!canManage) {
    return (
      <EducationPageChrome title={t('newInterventionReferral')}>
        <Alert type="warning" showIcon message={t('unavailable', { status: 403 })} />
      </EducationPageChrome>
    );
  }

  return (
    <EducationPageChrome
      title={t('newInterventionReferral')}
      description={t('interventionIntro')}
      extra={<Button onClick={() => navigate('/education/intervention')}>{t('intervention')}</Button>}
    >
      <Card size="small" style={{ maxWidth: 640 }}>
        <Alert type="info" showIcon style={{ marginBottom: 16 }} message={t('interventionHumanApprovedHint')} />
        <Form
          layout="vertical"
          initialValues={{ referral_on: dayjs(), source_type: 'manual' }}
          onFinish={(values) => create.mutate(values)}
        >
          <Form.Item name="student_id" label={t('student')} rules={[{ required: true }]}>
            <EducationStudentSelect />
          </Form.Item>
          <Form.Item name="section_id" label={t('section')}>
            <EducationSectionSelect allowClear />
          </Form.Item>
          <Form.Item name="referral_on" label={t('referralOn')} rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} format="DD-MMM-YYYY" />
          </Form.Item>
          <Form.Item name="source_type" label={t('interventionSource')} rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'manual', label: t('sourceManual') },
                { value: 'inclusion_screening', label: t('sourceInclusionScreening') },
                { value: 'behaviour_incident', label: t('sourceBehaviourIncident') },
              ]}
            />
          </Form.Item>
          <Form.Item name="source_id" label={t('sourceIdOptional')}>
            <Input placeholder={t('sourceIdHint')} />
          </Form.Item>
          <Form.Item name="referral_reason" label={t('referralReason')} rules={[{ required: true }]}>
            <Input.TextArea rows={3} maxLength={500} />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={create.isPending}>
            {t('save')}
          </Button>
        </Form>
      </Card>
    </EducationPageChrome>
  );
}
